import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { systemApi } from '../api/system';
import { queryKeys } from '../lib/queryKeys';
import type { PaginationParams } from '../types/api';
import type { AdminFeatureFlagCreateRequest, AdminFeatureFlagUpdateRequest } from '../types/system';

export function useFeatureFlags() {
  return useQuery({ queryKey: queryKeys.system.featureFlags, queryFn: () => systemApi.listFeatureFlags() });
}

export function useCreateFeatureFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AdminFeatureFlagCreateRequest) => systemApi.createFeatureFlag(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: queryKeys.system.featureFlags }); message.success('Feature flag created'); },
  });
}

export function useUpdateFeatureFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AdminFeatureFlagUpdateRequest }) => systemApi.updateFeatureFlag(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: queryKeys.system.featureFlags }); message.success('Feature flag updated'); },
  });
}

export function useDeleteFeatureFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => systemApi.deleteFeatureFlag(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: queryKeys.system.featureFlags }); message.success('Feature flag deleted'); },
  });
}

export function useAuditLogs(params: PaginationParams & { admin_id?: string; action?: string; target_type?: string }) {
  return useQuery({ queryKey: queryKeys.system.auditLogs(params), queryFn: () => systemApi.listAuditLogs(params) });
}

export function useAuditLogDetail(id: string) {
  return useQuery({ queryKey: queryKeys.system.auditLogDetail(id), queryFn: () => systemApi.auditLogDetail(id), enabled: !!id });
}

export function useSystemHealth() {
  return useQuery({ queryKey: queryKeys.system.health, queryFn: () => systemApi.health(), refetchInterval: 30_000 });
}

export function useSystemMetrics() {
  return useQuery({ queryKey: queryKeys.system.metrics, queryFn: () => systemApi.metrics(), refetchInterval: 30_000 });
}
